import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { useTranslation } from "react-i18next";
import "./PlayerSelectionDialog.css";

export const PlayerSelectionDialog = ({
  open,
  onClose,
  players,
  selectedPlayers,
  onSelectPlayers,
  confirmDisabled = false,
  confirmLabel = null,
}) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery("(max-width:640px)");
  const [searchQuery, setSearchQuery] = useState("");
  const [classementFilter, setClassementFilter] = useState([]);
  const [localSelected, setLocalSelected] = useState([]);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (open) {
      setLocalSelected(selectedPlayers || []);
      setSearchQuery("");
      setClassementFilter([]);
    }
  }, [open, selectedPlayers]);

  const availableClassements = useMemo(() => {
    if (!players || !Array.isArray(players)) return [];
    const classements = new Set();
    players.forEach((player) => {
      if (player.fltt_classement) classements.add(player.fltt_classement);
    });
    return Array.from(classements).sort();
  }, [players]);

  const filteredPlayers = useMemo(() => {
    if (!players || !Array.isArray(players)) return [];

    return players.filter((player) => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const fullName = `${player.user_first_name || ""} ${player.user_last_name || ""}`.toLowerCase();
        const alias = (player.user_alias || "").toLowerCase();
        if (!fullName.includes(query) && !alias.includes(query)) return false;
      }

      if (classementFilter.length > 0) {
        if (!player.fltt_classement || !classementFilter.includes(player.fltt_classement)) {
          return false;
        }
      }

      return true;
    });
  }, [players, searchQuery, classementFilter]);

  const handleTogglePlayer = (playerId) => {
    setLocalSelected((prev) =>
      prev.includes(playerId) ? prev.filter((id) => id !== playerId) : [...prev, playerId]
    );
  };

  const handleSelectAll = () => {
    const allFilteredIds = filteredPlayers
      .map((p) => p.player_id || p.user_id)
      .filter(Boolean);
    setLocalSelected((prev) => {
      const next = [...prev];
      allFilteredIds.forEach((id) => {
        if (!next.includes(id)) next.push(id);
      });
      return next;
    });
  };

  const handleDeselectAll = () => {
    const allFilteredIds = filteredPlayers
      .map((p) => p.player_id || p.user_id)
      .filter(Boolean);
    setLocalSelected((prev) => prev.filter((id) => !allFilteredIds.includes(id)));
  };

  const toggleClassement = (value) => {
    setClassementFilter((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const handleConfirm = async () => {
    if (confirmDisabled || confirming) return;
    setConfirming(true);
    try {
      await Promise.resolve(onSelectPlayers(localSelected));
      onClose();
    } catch (error) {
      console.error("Error confirming player selection:", error);
    } finally {
      setConfirming(false);
    }
  };

  const handleCancel = () => {
    if (confirming || confirmDisabled) return;
    setLocalSelected(selectedPlayers || []);
    onClose();
  };

  const getInitials = (player) => {
    const firstName = player.user_first_name || "";
    const lastName = player.user_last_name || "";
    if (firstName && lastName) return (firstName[0] + lastName[0]).toUpperCase();
    if (firstName) return firstName.slice(0, 2).toUpperCase();
    if (player.user_alias) return player.user_alias.slice(0, 2).toUpperCase();
    return "??";
  };

  const getPlayerName = (player) => {
    if (player.user_first_name && player.user_last_name) {
      return `${player.user_first_name} ${player.user_last_name}`;
    }
    return player.user_alias || t("unknownPlayer") || "Unknown Player";
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      fullScreen={isMobile}
      fullWidth
      maxWidth="sm"
      className="psd-dlg"
      PaperProps={{
        className: `psd-dlg__paper${isMobile ? " psd-dlg__paper--mobile" : ""}`,
      }}
      slotProps={{
        backdrop: { className: "psd-dlg__backdrop" },
      }}
    >
      <div className="psd-dlg__shell">
        {isMobile && <div className="psd-dlg__handle" aria-hidden />}

        <header className="psd-dlg__header">
          <div className="psd-dlg__header-text">
            <h2 className="psd-dlg__title">
              {t("selectPlayersForGroup") || "Select Players for Group"}
            </h2>
            <p className="psd-dlg__subtitle">
              {localSelected.length} {t("selected") || "selected"}
              {" · "}
              {filteredPlayers.length} {t("filtered") || "filtered"}
            </p>
          </div>
          <IconButton
            aria-label={t("close") || "Close"}
            onClick={handleCancel}
            className="psd-dlg__close"
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </header>

        <div className="psd-dlg__toolbar">
          <label className="psd-dlg__search">
            <SearchIcon className="psd-dlg__search-icon" fontSize="small" />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t("searchPlayersByName") || "Search by name..."}
              className="psd-dlg__search-input"
              autoComplete="off"
            />
          </label>

          {availableClassements.length > 0 && (
            <div className="psd-dlg__filters" role="group" aria-label={t("classement") || "Classement"}>
              <button
                type="button"
                className={`psd-dlg__filter-chip${!classementFilter.length ? " is-active" : ""}`}
                onClick={() => setClassementFilter([])}
              >
                {t("all") || "All"}
              </button>
              {availableClassements.map((classement) => (
                <button
                  key={classement}
                  type="button"
                  className={`psd-dlg__filter-chip${
                    classementFilter.includes(classement) ? " is-active" : ""
                  }`}
                  onClick={() => toggleClassement(classement)}
                >
                  {classement}
                </button>
              ))}
            </div>
          )}

          <div className="psd-dlg__bulk">
            <button
              type="button"
              className="psd-dlg__bulk-btn"
              onClick={handleSelectAll}
              disabled={filteredPlayers.length === 0}
            >
              {t("selectAll") || "Select all"}
            </button>
            <button
              type="button"
              className="psd-dlg__bulk-btn"
              onClick={handleDeselectAll}
              disabled={filteredPlayers.length === 0}
            >
              {t("deselectAll") || "Deselect all"}
            </button>
          </div>
        </div>

        <DialogContent className="psd-dlg__body">
          {filteredPlayers.length === 0 ? (
            <div className="psd-dlg__empty">
              {t("noPlayersFound") || "No players found"}
            </div>
          ) : (
            <ul className="psd-dlg__list">
              {filteredPlayers.map((player) => {
                const playerId = player.player_id || player.user_id;
                const isSelected = localSelected.includes(playerId);
                return (
                  <li key={playerId}>
                    <button
                      type="button"
                      className={`psd-dlg__row${isSelected ? " is-selected" : ""}`}
                      onClick={() => handleTogglePlayer(playerId)}
                      aria-pressed={isSelected}
                    >
                      <span className="psd-dlg__check" aria-hidden>
                        {isSelected ? (
                          <CheckCircleIcon fontSize="small" />
                        ) : (
                          <RadioButtonUncheckedIcon fontSize="small" />
                        )}
                      </span>
                      {player.user_image ? (
                        <img
                          className="psd-dlg__avatar"
                          src={player.user_image}
                          alt=""
                        />
                      ) : (
                        <span className="psd-dlg__avatar psd-dlg__avatar--fallback">
                          {getInitials(player)}
                        </span>
                      )}
                      <span className="psd-dlg__row-body">
                        <span className="psd-dlg__name">{getPlayerName(player)}</span>
                        <span className="psd-dlg__meta">
                          {player.fltt_classement && (
                            <span className="psd-dlg__badge">{player.fltt_classement}</span>
                          )}
                          {player.fltt_ranking_points != null && player.fltt_ranking_points !== "" && (
                            <span className="psd-dlg__points">
                              {player.fltt_ranking_points} {t("points") || "pts"}
                            </span>
                          )}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </DialogContent>

        <footer className="psd-dlg__footer">
          <button
            type="button"
            className="psd-dlg__btn psd-dlg__btn--ghost"
            onClick={handleCancel}
            disabled={confirming || confirmDisabled}
          >
            {t("cancel") || "Cancel"}
          </button>
          <button
            type="button"
            className="psd-dlg__btn psd-dlg__btn--primary"
            onClick={handleConfirm}
            disabled={confirming || confirmDisabled}
          >
            {confirmLabel ||
              `${t("confirm") || "Confirm"}${
                localSelected.length > 0 ? ` (${localSelected.length})` : ""
              }`}
          </button>
        </footer>
      </div>
    </Dialog>
  );
};
